import { de, enUS, es, fr, it, ru } from 'date-fns/locale';
import { noop } from 'lodash/fp';
import { func, instanceOf, number, shape, string } from 'prop-types';
import React from 'react';
import { registerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from 'static';

import { StyledErrorMessage, StyledLabel } from '../../styled';
import { SharedStyledDateIcon } from '../styled';
import { StyledDatePicker, StyledPickerWrapper } from './styled';

// Register all locales
registerLocale('en', enUS);
registerLocale('de', de);
registerLocale('fr', fr);
registerLocale('it', it);
registerLocale('es', es);
registerLocale('ru', ru);

const DatePicker = ({
  name,
  date,
  handleChange,
  label,
  minDate,
  maxDate,
  error,
  popperModifiers = {},
}) => {
  const { t, i18n } = useTranslation();

  // Get current locale, fallback to 'en' if not supported
  const currentLocale = ['en', 'de', 'fr', 'it', 'es', 'ru'].includes(
    i18n.language,
  )
    ? i18n.language
    : 'en';

  const CalendarContainer = ({ className, children }) => {
    return (
      <div
        style={{
          height: '100%',
          padding: '16px',
          backgroundColor: '#ffffff',
          color: '#304659',
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          transition: 'all',
          gap: '8px',
          boxShadow: '0px 0px 8px 2px #cfcfcf',
        }}
      >
        <div
          className={className}
          style={{
            borderRadius: '8px',
            borderColor: '#ffffff',
          }}
        >
          {children}
        </div>
      </div>
    );
  };

  return (
    <>
      <StyledPickerWrapper>
        <StyledLabel htmlFor={name}>{label}</StyledLabel>

        <StyledDatePicker
          id={name || false}
          selected={date}
          dateFormat="dd.MM.yyyy"
          minDate={minDate}
          maxDate={maxDate}
          error={error}
          popperPlacement="bottom-left"
          popperModifiers={popperModifiers}
          onChange={handleChange}
          calendarContainer={CalendarContainer}
          locale={currentLocale}
          calendarStartDay={1}
        />

        <SharedStyledDateIcon src={CalendarIcon} alt="" />
      </StyledPickerWrapper>

      {error && (
        <StyledErrorMessage>
          {error.message || t(`errors.${error.type}`)}
        </StyledErrorMessage>
      )}
    </>
  );
};

DatePicker.propTypes = {
  name: string,
  date: instanceOf(Date) || number,
  minDate: instanceOf(Date),
  maxDate: instanceOf(Date),
  handleChange: func,
  label: string,
  error: shape({}),
};

DatePicker.defaultProps = {
  date: new Date(),
  minDate: null,
  maxDate: null,
  handleChange: noop,
  label: '',
  error: null,
  name: '',
};

export default DatePicker;
